import { useCallback } from 'react';
import { useAskMagicApron } from '@thd-olt-component-react/magic-apron';
import { stripMarkdown } from '../utils/stripMarkdown';

/**
 * Adapts Magic Apron's `useAskMagicApron` to the single callback the layouts click with.
 *
 * Magic Apron owns the handoff and everything analytics reads from it — session, page type,
 * attribution — and exposes two actions: `askQuestion` hands a question over for the assistant to
 * answer, `openAssistant` opens the drawer with nothing prepopulated. The banner supplies only what
 * Magic Apron cannot know, and picks between the two, so a layout keeps calling one handler.
 *
 * The `suggestedQuestions` API returns question text as markdown, which the assistant must not
 * receive, so it is reduced to the plain text the shopper read before it is handed over. A question
 * that reduces to nothing is no question at all: it opens an empty drawer rather than handing over
 * markup, which is the same thing `Ask your own question` does by passing no text at all.
 *
 * @param {Object} [options]
 * @param {string} [options.entryLocationForAnalytics] - Where the entry point lives, for analytics.
 * @param {boolean} [options.enabled] - When false, Magic Apron opens no channel and both actions
 * no-op, so a click costs nothing while the banner is switched off.
 * @returns {function(string=, number=): Promise<void>} Called with (markdown, position). Resolves
 * once Magic Apron is done with the click — it never rejects, so a caller with nothing to do after
 * the handoff can ignore it, as the layouts do.
 */
export const useAskBannerQuestion = ({ entryLocationForAnalytics = '', enabled = true } = {}) => {
  const { askQuestion, openAssistant } = useAskMagicApron({ entryLocationForAnalytics, enabled });

  return useCallback((question, position) => {
    const plainQuestion = stripMarkdown(question);

    if (!plainQuestion) {
      openAssistant();
      return Promise.resolve();
    }

    // `position` records where in the list the question sat, and is only meaningful with one.
    return askQuestion(plainQuestion, { position });
  }, [askQuestion, openAssistant]);
};
